import { z } from "zod";

import { directorySearchSchema, specialistsSearchSchema } from "@/features/directory/utils/search-params";
import { timeFiltersSearchSchema } from "@/features/directory/utils/time-filters";
import { EventFormat } from "@complete-athlete/enums";

export const EVENT_SORTS = ["soonest", "distance", "price"] as const;
export type EventSort = (typeof EVENT_SORTS)[number];

/**
 * Finding an event (ADR 0052): the same what and where as the specialists page, and what only an
 * event has. They live in the URL, so a filtered view can be linked and survives a reload.
 */
export const eventsSearchSchema = directorySearchSchema
  .extend(timeFiltersSearchSchema.pick({ when: true, date: true }).shape)
  .extend({
    format: z.enum(EventFormat).optional().catch(undefined),
    priceMax: specialistsSearchSchema.shape.priceMax,
    /** Only the businesses this person books with. */
    mine: z.literal(true).optional().catch(undefined),
    /** The hearted events instead of all of them. */
    tab: z.literal("favorites").optional().catch(undefined),
    sort: z.enum(EVENT_SORTS).optional().catch(undefined),
  });
export type EventsSearch = z.infer<typeof eventsSearchSchema>;

/** Whether anything narrows the list, so that "nothing here" is about the search, not the platform. */
export const eventFiltersOn = ({ tab: _tab, sort: _sort, ...filters }: EventsSearch) =>
  Object.values(filters).some((value) => value !== undefined);

/**
 * A search as the API takes it: strings for the query string, and defaults left out. The favorites
 * are already chosen, so where, when and how much would only hide them.
 */
export const eventsApiQuery = (search: EventsSearch, page = 1) => ({
  ...(search.tab === "favorites"
    ? { favorites: "true" as const }
    : {
        ...(search.service ? { service: search.service } : {}),
        ...(search.q ? { q: search.q } : {}),
        ...(search.city ? { city: search.city } : {}),
        ...(search.near ? { near: search.near } : {}),
        ...(search.online ? { online: "true" as const } : {}),
        ...(search.format ? { format: search.format } : {}),
        ...(search.priceMax ? { priceMax: String(search.priceMax * 100) } : {}),
        ...(search.when ? { when: search.when } : {}),
        ...(search.when === "date" && search.date ? { date: search.date } : {}),
        ...(search.mine ? { mine: "true" as const } : {}),
      }),
  sort: search.sort ?? "soonest",
  page: String(page),
});
